import type { HangingProtocol } from "@shared/lib/hanging-protocol"

interface ProtocolActionsParams {
  applyLayoutName: (value: string) => void
  scene: {
    setLayout: (layout: string) => void
  }
  ensureLayoutViewerCount: (layout: string) => void
  createProtocol: {
    isPending: boolean
    mutateAsync: (payload: {
      name: string
      layout_key: string
      slots: Array<{
        viewer_index: number
        text_contains?: string | null
      }>
      is_default?: boolean
    }) => Promise<{ id: string }>
  }
  updateProtocol: {
    isPending: boolean
    mutateAsync: (payload: {
      protocolId: string
      payload: {
        name?: string
        layout_key?: string
        slots?: Array<{
          viewer_index: number
          text_contains?: string | null
        }>
        is_default?: boolean
      }
    }) => Promise<unknown>
  }
  deleteProtocol: {
    isPending: boolean
    mutateAsync: (payload: { protocolId: string }) => Promise<unknown>
  }
  selectedProtocol: HangingProtocol | null
  applySelectedProtocolId: (value: string | null) => void
}

export function buildProtocolActions({
  applyLayoutName,
  scene,
  ensureLayoutViewerCount,
  createProtocol,
  updateProtocol,
  deleteProtocol,
  selectedProtocol,
  applySelectedProtocolId
}: ProtocolActionsParams) {
  const handleLayoutChange = (nextLayout: string) => {
    applyLayoutName(nextLayout)
    scene.setLayout(nextLayout)
    ensureLayoutViewerCount(nextLayout)
  }

  const handleCreateProtocol = async (payload: {
    name: string
    layout_key: string
    slots: Array<{
      viewer_index: number
      text_contains?: string | null
    }>
  }) => {
    const created = await createProtocol.mutateAsync({
      ...payload,
      is_default: false
    })
    applySelectedProtocolId(created.id)
  }

  const handleEditProtocol = async (payload: {
    name: string
    layout_key: string
    slots: Array<{
      viewer_index: number
      text_contains?: string | null
    }>
  }) => {
    if (!selectedProtocol) return
    await updateProtocol.mutateAsync({
      protocolId: selectedProtocol.id,
      payload
    })
  }

  const handleSetDefault = async () => {
    if (!selectedProtocol) return
    await updateProtocol.mutateAsync({
      protocolId: selectedProtocol.id,
      payload: { is_default: true }
    })
  }

  const handleDeleteProtocol = async () => {
    if (!selectedProtocol) return
    const confirmed = window.confirm(`Delete protocol "${selectedProtocol.name}"?`)
    if (!confirmed) return
    await deleteProtocol.mutateAsync({ protocolId: selectedProtocol.id })
    applySelectedProtocolId(null)
  }

  const isMutating = createProtocol.isPending || updateProtocol.isPending || deleteProtocol.isPending

  return {
    handleLayoutChange,
    handleCreateProtocol,
    handleEditProtocol,
    handleSetDefault,
    handleDeleteProtocol,
    isMutating
  }
}
